import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { FacturaPorConciliar, OrdenSinFactura } from '@manejo-alcanza/contracts';
import { conciliarSinDuda, enlazarOrdenes, pesos, verConciliacion } from '../api';
import { Aviso, Boton, Card, Error_ } from './basicos';

/**
 * Qué factura va con qué orden de compra.
 *
 * **Es la pieza que faltaba de F6.** Hasta ahora las órdenes solo se podían
 * indicar al *crear* una factura, y las que llegan de Siigo se crean sin
 * ninguna —Siigo no sabe a qué orden corresponde una factura—, así que no había
 * forma de enlazarlas nunca. La pantalla de la factura decía «falta enlazarla»
 * y no existía el botón.
 *
 * ## Por qué es una bandeja y no dos listas
 *
 * Dos listas lado a lado, facturas y órdenes, dejan el trabajo de emparejar en
 * los ojos de quien mira. Aquí el sistema ya hizo el trabajo: lee el número de
 * orden que quien emite escribe en las observaciones de Siigo y **propone**. Lo
 * único que queda es confirmar, y cuando no hay nada que confirmar ni eso.
 *
 * ## Las dos mitades
 *
 * Facturas sin orden es la mitad que al menos se veía. **Órdenes entregadas sin
 * factura es la que no se veía en ningún sitio**, y es mercancía que salió y no
 * se cobró.
 */
export function Conciliacion(): ReactNode {
  const cliente = useQueryClient();
  const bandeja = useQuery({ queryKey: ['conciliacion'], queryFn: verConciliacion });

  const sinDuda = useMutation({
    mutationFn: conciliarSinDuda,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['conciliacion'] });
      void cliente.invalidateQueries({ queryKey: ['facturas'] });
    },
  });

  if (bandeja.isLoading) return <p className="text-sm text-tenue">Cargando…</p>;
  if (bandeja.isError) return <Error_>{(bandeja.error as Error).message}</Error_>;

  const { facturasSinOrden, ordenesSinFactura, facturasTotales } = bandeja.data!;
  const claras = facturasSinOrden.filter((f) => f.sugerencia.clase === 'ENLAZAR').length;

  return (
    <div className="space-y-5">
      <Card titulo="Facturas sin orden enlazada">
        <p className="mb-3 text-sm text-tenue">
          {facturasSinOrden.length === 0
            ? `Ninguna. Las ${facturasTotales} facturas tienen su orden.`
            : `${facturasSinOrden.length} de ${facturasTotales}. Mientras sigan así no se puede
               comparar lo facturado contra lo que el cliente recibió.`}
        </p>

        {claras > 0 && (
          <div className="mb-4">
            <Boton onClick={() => sinDuda.mutate()} deshabilitado={sinDuda.isPending}>
              {sinDuda.isPending ? 'Enlazando…' : `Enlazar las ${claras} que no tienen duda`}
            </Boton>
            <p className="mt-1 text-xs text-tenue">
              Solo esas: una única orden de ese cliente con ese número <strong>y</strong> el valor
              cuadrando. El resto sigue esperando a que decidas tú.
            </p>
          </div>
        )}

        {sinDuda.isError && (
          <div className="mb-3">
            <Error_>{(sinDuda.error as Error).message}</Error_>
          </div>
        )}

        <ul className="space-y-3">
          {facturasSinOrden.map((f) => (
            <Pendiente key={f.facturaId} factura={f} />
          ))}
        </ul>
      </Card>

      <Card titulo="Órdenes entregadas sin factura">
        {/*
          La mitad que nadie estaba mirando. Una factura sin orden al menos se
          veía en la pantalla de la factura; una orden entregada que nadie
          facturó no aparecía en ningún sitio, y eso es plata que salió.
        */}
        {ordenesSinFactura.length === 0 ? (
          <p className="text-sm text-tenue">Ninguna. Todo lo entregado está facturado.</p>
        ) : (
          <>
            <Aviso>
              <strong>Mercancía que salió y no se ha cobrado.</strong> {ordenesSinFactura.length}{' '}
              {ordenesSinFactura.length === 1 ? 'orden entregada' : 'órdenes entregadas'} sin
              ninguna factura enlazada.
            </Aviso>
            <ul className="mt-3 space-y-1 text-sm">
              {ordenesSinFactura.map((o) => (
                <SinFactura key={o.ordenId} orden={o} />
              ))}
            </ul>
          </>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Pendiente({ factura }: { factura: FacturaPorConciliar }): ReactNode {
  const cliente = useQueryClient();
  const s = factura.sugerencia;

  const enlazar = useMutation({
    mutationFn: (ordenId: string) =>
      enlazarOrdenes(factura.facturaId, [{ ordenId, valorImputado: factura.valorTotal }]),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['conciliacion'] });
      void cliente.invalidateQueries({ queryKey: ['facturas'] });
    },
  });

  return (
    <li className="rounded-md border border-borde p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span>
          <Link
            to={`/plata/${factura.facturaId}`}
            className="font-medium text-acento hover:underline"
          >
            {factura.numero}
          </Link>
          <span className="text-tenue"> · {factura.clienteNombre}</span>
          <span className="text-tenue"> · {factura.fechaEmision}</span>
        </span>
        <span className="cifra">{pesos(factura.valorTotal)}</span>
      </div>

      {/* El texto crudo de Siigo, tal cual. Es de donde sale el número, y
          enseñarlo es lo que permite entender por qué el sistema propuso eso. */}
      {factura.observaciones !== null && (
        <p className="mt-1 text-xs text-tenue">
          Siigo dice: <span className="font-mono">«{factura.observaciones}»</span>
        </p>
      )}

      <div className="mt-2 text-sm">
        {s.clase === 'ENLAZAR' && (
          <>
            <p>
              <span className="text-exito">✓</span> Orden{' '}
              <Link to={`/ordenes/${s.orden.ordenId}`} className="text-acento hover:underline">
                {s.orden.numero}
              </Link>
              , entrega {s.orden.fechaEntregaMax}. El valor cuadra con lo{' '}
              {s.contra === 'RECIBIDO' ? 'recibido' : 'pedido'}.
            </p>
            <div className="mt-2">
              <Boton
                onClick={() => enlazar.mutate(s.orden.ordenId)}
                deshabilitado={enlazar.isPending}
              >
                Es esta
              </Boton>
            </div>
          </>
        )}

        {s.clase === 'PROPONER' && (
          <>
            <p>
              Orden{' '}
              <Link to={`/ordenes/${s.orden.ordenId}`} className="text-acento hover:underline">
                {s.orden.numero}
              </Link>
              , entrega {s.orden.fechaEntregaMax}.{' '}
              <span className="text-alerta">{s.reparo}</span>
            </p>
            <p className="mt-0.5 text-xs text-tenue">
              Pedido {pesos(s.orden.valorPedido)} · recibido{' '}
              {s.orden.valorRecibido === null ? 'no se sabe' : pesos(s.orden.valorRecibido)}
            </p>
            <div className="mt-2">
              <Boton
                tipo="secundario"
                onClick={() => enlazar.mutate(s.orden.ordenId)}
                deshabilitado={enlazar.isPending}
              >
                Enlazar igual
              </Boton>
            </div>
          </>
        )}

        {s.clase === 'VARIAS' && (
          <>
            <p className="text-alerta">
              Hay {s.ordenes.length} órdenes con el número {s.numero}. No se elige sola.
            </p>
            <ul className="mt-2 space-y-1">
              {s.ordenes.map((o) => (
                <li key={o.ordenId} className="flex flex-wrap items-center gap-2">
                  <span>
                    {o.numero} · entrega {o.fechaEntregaMax} · {pesos(o.valorPedido)}
                  </span>
                  <Boton
                    tipo="secundario"
                    onClick={() => enlazar.mutate(o.ordenId)}
                    deshabilitado={enlazar.isPending}
                  >
                    Es esta
                  </Boton>
                </li>
              ))}
            </ul>
          </>
        )}

        {s.clase === 'SIN_ORDEN' && (
          <p className="text-tenue">
            Dice la orden {s.numero} y no hay ninguna de este cliente con ese número. O falta
            cargarla, o el número está mal escrito en Siigo.
          </p>
        )}

        {s.clase === 'SIN_NUMERO' && (
          <p className="text-tenue">
            Las observaciones no mencionan ninguna orden. Esta hay que enlazarla a mano desde la
            factura.
          </p>
        )}
      </div>

      {enlazar.isError && (
        <div className="mt-2">
          <Error_>{(enlazar.error as Error).message}</Error_>
        </div>
      )}
    </li>
  );
}

function SinFactura({ orden }: { orden: OrdenSinFactura }): ReactNode {
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-2">
      <span>
        <Link to={`/ordenes/${orden.ordenId}`} className="font-medium text-acento hover:underline">
          {orden.numero}
        </Link>
        <span className="text-tenue"> · {orden.clienteNombre}</span>
        <span className="text-tenue">
          {' '}
          · entregada hace {orden.diasDesdeEntrega} {orden.diasDesdeEntrega === 1 ? 'día' : 'días'}
        </span>
      </span>
      {/* Lo recibido manda sobre lo pedido: es sobre eso que se factura. Si
          todavía no se sabe, se dice, no se pone el pedido en su lugar. */}
      <span className="cifra">
        {orden.valorRecibido === null ? (
          <span className="text-tenue">no se sabe cuánto recibieron</span>
        ) : (
          pesos(orden.valorRecibido)
        )}
      </span>
    </li>
  );
}
